// One candidate marketplace ad in the "published versions" search modal.
//
// ## Why this row exists rather than reusing CondensedListingRowContent
//
// That component (signatures/NewListingPicker) is reused verbatim by the LINKED-source
// cards, where the data really is a hydrated `ProcessedListingData`. A candidate is not:
// it is a lean server projection, and satisfying that prop type would mean casting a
// half-built object into a full listing — a cast that lies, and one that would break
// silently the day the component reads a field the projection does not carry. So the
// visual grammar is copied (56px thumbnail, subtitle2 address, caption meta line) and
// the data contract is not.
//
// ## The row's whole job is to be explainable
//
// A picker that cannot say WHY a row is on screen gets clicked wrongly, and a wrong link
// hides a market ad she wanted. Three things carry that:
//
//  - the match badge and `matchedOn` — the fields that positively agreed;
//  - the contradiction reasons, which name the DISAGREEING VALUES ("רשום 3.5 חדרים,
//    בנכס שלך 4") rather than the field. A contradicted candidate used to be deleted by a
//    `score > 0` filter while the realtor was looking at that very ad in another tab;
//  - `linkedTo`, which says WHICH of her listings already holds the ad. The old boolean
//    could only grey the button out, which reads as a bug.
//
// ## "Already linked elsewhere" is an ACTION, not a refusal
//
// `linkedTo.listingId` exists precisely so this state can be worked, and rendering it as
// a caption above a disabled button threw that away: with dozens of listings the only way
// left to find and free the ad is to open them one by one — the same dead end the greyed
// button was supposed to replace. So the row NAMES the holding listing, links to it, and
// offers the move directly. Moving is deliberately the primary action: an ad attached to
// the wrong listing is a mistake she is trying to correct, not one to navigate around.
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { ImageNotSupported, LinkOff, OpenInNew, SwapHoriz } from "@material-ui/icons";
import Label from "components/Label";
import { numberToCurrency } from "utils/listings/display";
import { STALENESS_LABEL_KEY, listingStaleness } from "./sourceListingStaleness";
import type { REST_schema } from "../../../../@types/sharedSchema";

const ORIGIN_LABEL: Record<string, string> = { yad2: "יד2", madlan: "מדלן", webot: "Webot" };

type SourceCandidateRowProps = {
  candidate: REST_schema.ListingSourceCandidate;
  /** The listing's own comparable values — the other half of every contradiction line. */
  seed: REST_schema.ListingSourceCandidateSeed | null;
  /** True when this ad is linked to the listing the picker is open on. */
  linkedHere: boolean;
  /**
   * The holding listing's address, once resolved. Null while it is still being read —
   * the row then falls back to the unnamed wording rather than rendering an empty link.
   */
  holdingListingLabel?: string | null;
  busy: boolean;
  disabled: boolean;
  onLink: (candidate: REST_schema.ListingSourceCandidate) => void;
  onUnlink: (candidate: REST_schema.ListingSourceCandidate) => void;
  /** Detaches the ad from `fromListingId` and attaches it here, in one action. */
  onMoveLink: (candidate: REST_schema.ListingSourceCandidate, fromListingId: string) => void;
};

const formatNumber = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));

export default function SourceCandidateRow({
  candidate,
  seed,
  linkedHere,
  holdingListingLabel,
  busy,
  disabled,
  onLink,
  onUnlink,
  onMoveLink
}: SourceCandidateRowProps): JSX.Element {
  const { t: translate } = useTranslation();
  const theme = useTheme();

  const contradicted = candidate.contradictions.length > 0;
  // Linked to a DIFFERENT listing of hers. Named, never hidden: an ad attached to the
  // wrong listing is a mistake she is about to repeat, and a row she cannot see is a
  // row she cannot explain the absence of.
  //
  // `isThisListing` is what keeps an unlink made seconds ago from reading as "linked
  // elsewhere": the server's claim map is cached for 30s, so it can still report this
  // very listing as the holder while the form already knows better.
  const holdingListingId =
    candidate.linkedTo && !linkedHere && !candidate.linkedTo.isThisListing
      ? candidate.linkedTo.listingId
      : null;

  /**
   * Where the holding listing lives, opened in a NEW TAB.
   *
   * Deliberately a link and not `openListingDialog`: the app's listing dialog is driven
   * by ONE provider whose state also feeds the `NewListingDialog` this picker is
   * rendered inside, so opening the holder in-app can swap the document being edited
   * out from under her unsaved form. A new tab cannot touch it.
   *
   * `/dashboard/listings/:id` is the app's existing deep link — the same one the server
   * puts in WhatsApp notifications.
   */
  const holdingListingHref = holdingListingId ? `/dashboard/listings/${holdingListingId}` : null;

  const meta = [
    candidate.price ? numberToCurrency(candidate.price) : null,
    candidate.roomsCount
      ? translate("listingsPage.sourcePicker.rooms", { count: candidate.roomsCount })
      : null,
    candidate.squareMeter ? translate("listingsPage.sourcePicker.sqm", { value: candidate.squareMeter }) : null,
    typeof candidate.floor === "number"
      ? translate("listingsPage.sourcePicker.floor", { value: candidate.floor })
      : null,
    candidate.propertyType
  ]
    .filter(Boolean)
    .join(" · ");

  const publisher = [candidate.agencyName, candidate.sellerName].filter(Boolean).join(" · ");

  const staleness = listingStaleness(candidate.origin, candidate.updatedTimeMs);

  /**
   * A contradiction, in words, with both values.
   *
   * Numeric fields print the pair because that is what makes an override informed. The
   * text fields name the difference instead — the candidate's own street and city are
   * already on the row directly above this line, so repeating them would be noise.
   */
  const contradictionReason = (field: string): string => {
    switch (field) {
      case "roomsCount":
        return translate("listingsPage.sourcePicker.reason.rooms", {
          theirs: candidate.roomsCount !== null ? formatNumber(candidate.roomsCount) : "—",
          ours: seed?.rooms != null ? formatNumber(seed.rooms) : "—"
        });
      case "squareMeter":
        return translate("listingsPage.sourcePicker.reason.size", {
          theirs: candidate.squareMeter ?? "—",
          ours: seed?.sizeOfPropertyM2 ?? "—"
        });
      case "houseNumber":
        return translate("listingsPage.sourcePicker.reason.houseNumber", {
          theirs: candidate.houseNumber ?? "—",
          ours: seed?.streetNumber ?? "—"
        });
      case "street":
        return translate("listingsPage.sourcePicker.reason.street", { ours: seed?.street ?? "—" });
      case "city":
        return translate("listingsPage.sourcePicker.reason.city", { ours: seed?.city ?? "—" });
      case "transactionType":
        return translate("listingsPage.sourcePicker.reason.transactionType");
      default:
        return translate(`listingsPage.sourcePicker.field.${field}`, field);
    }
  };

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: linkedHere ? "primary.light" : contradicted ? "warning.light" : "divider",
        bgcolor: linkedHere ? alpha(theme.palette.primary.main, 0.04) : "background.paper",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "background.neutral",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: candidate.coverImageUrl ? `url(${candidate.coverImageUrl})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        {!candidate.coverImageUrl && <ImageNotSupported sx={{ color: "text.disabled", fontSize: 22 }} />}
      </Box>

      <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" sx={{ gap: 0.75, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
            {candidate.address || translate("listingsPage.sourcePicker.unknownAddress")}
          </Typography>
          <Chip size="small" label={ORIGIN_LABEL[candidate.origin] ?? candidate.origin} sx={{ flexShrink: 0 }} />
          {candidate.originalListingUrl && (
            // A separate hit area from the link button on purpose: opening the ad and
            // attaching it to the listing are not the same decision.
            <Tooltip title={translate("listingsPage.sourcePicker.openAd") as string}>
              <IconButton
                size="small"
                component="a"
                href={candidate.originalListingUrl}
                target="_blank"
                rel="noopener"
                sx={{ flexShrink: 0 }}
              >
                <OpenInNew sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {meta && (
          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
            {meta}
          </Typography>
        )}
        {(candidate.neighborhood || publisher) && (
          <Typography variant="caption" sx={{ color: "text.disabled" }} noWrap>
            {[candidate.neighborhood, publisher].filter(Boolean).join(" · ")}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 0.75, pt: 0.25 }}>
          {contradicted ? (
            <>
              <Label color="warning" variant="ghost">
                {translate("listingsPage.sourcePicker.contradictsBadge")}
              </Label>
              <Typography variant="caption" sx={{ color: "warning.dark" }}>
                {candidate.contradictions.map(contradictionReason).join(" · ")}
              </Typography>
            </>
          ) : (
            <>
              <Label color={candidate.confident ? "primary" : "default"} variant="ghost">
                {translate("listingsPage.sourcePicker.matchScore", { score: candidate.score })}
              </Label>
              {candidate.matchedOn.length > 0 && (
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  {translate("listingsPage.sourcePicker.matchedOn", {
                    fields: candidate.matchedOn
                      .map((field) => translate(`listingsPage.sourcePicker.field.${field}`, field))
                      .join(", ")
                  })}
                </Typography>
              )}
            </>
          )}
          {staleness !== "fresh" && (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              {translate(STALENESS_LABEL_KEY[staleness])}
            </Typography>
          )}
          {holdingListingId && (
            <Typography variant="caption" sx={{ color: "info.dark" }}>
              {holdingListingLabel ? (
                <>
                  {translate("listingsPage.sourcePicker.linkedToNamedListing")}{" "}
                  {/* The name IS the way there. */}
                  <Link
                    href={holdingListingHref ?? undefined}
                    target="_blank"
                    rel="noopener"
                    underline="always"
                    color="inherit"
                    sx={{ fontWeight: 600 }}
                  >
                    {holdingListingLabel}
                  </Link>
                </>
              ) : (
                translate("listingsPage.sourcePicker.linkedToOtherListing")
              )}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack sx={{ flexShrink: 0, pt: 0.25 }}>
        {linkedHere ? (
          // Unlinking from the search list, not only from the linked cards: she is
          // looking at the row she wants gone, and making her close the modal to undo
          // the tap she just made is how a picker becomes a trap.
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            disabled={disabled || busy}
            startIcon={busy ? <CircularProgress size={13} /> : <LinkOff sx={{ fontSize: "16px !important" }} />}
            onClick={() => onUnlink(candidate)}
            sx={{ "&:hover": { color: "error.main", borderColor: "error.main" } }}
          >
            {translate("listingsPage.sourcePicker.linked")}
          </Button>
        ) : holdingListingId ? (
          // One market ad stands for one property, so it cannot simply be attached here
          // as well — but the answer to that is to MOVE it, not to disable the button and
          // leave her to hunt through her listings for the one holding it.
          <Stack sx={{ gap: 0.25, alignItems: "stretch" }}>
            <Tooltip title={translate("listingsPage.sourcePicker.moveLinkHint") as string}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  fullWidth
                  disabled={disabled || busy}
                  startIcon={
                    busy ? <CircularProgress size={13} /> : <SwapHoriz sx={{ fontSize: "17px !important" }} />
                  }
                  onClick={() => onMoveLink(candidate, holdingListingId)}
                >
                  {translate("listingsPage.sourcePicker.moveLinkHere")}
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              color="inherit"
              component="a"
              href={holdingListingHref ?? undefined}
              target="_blank"
              rel="noopener"
              sx={{ typography: "caption", color: "text.secondary" }}
            >
              {translate("listingsPage.sourcePicker.openHoldingListing")}
            </Button>
          </Stack>
        ) : (
          <Button
            size="small"
            variant="contained"
            disabled={disabled || busy}
            startIcon={busy ? <CircularProgress size={13} /> : undefined}
            onClick={() => onLink(candidate)}
          >
            {translate("listingsPage.sourcePicker.link")}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
